import { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { apigetPublic } from '../../service/api';
import { isInternalPath } from '../../utils/internalPaths';
import { isTypographyPreviewFrame } from '../../utils/typographyPreview';
import { typographyGroupForPath, setTypographyGroup } from '../../utils/typographyGroups';

// Turns the /dashboard/typography scale on for public marketing pages.
//
// The server injects <style id="jet-typography"> whose CSS variables are scoped
// to `html.jet-public`; this toggles that class by route so admin, sign-in and
// the borrower portal keep the theme's static fallbacks. The class lives on
// <html> (not a wrapper div) so MUI portals — menus, dialogs, the Apply modal —
// inherit the variables too. useLayoutEffect applies it before first paint.
//
// Mounted only in the logged-out route branch (App.js); unmounting (e.g. on
// sign-in) removes the class.

const SCOPE_CLASS = 'jet-public';
const STYLE_ID = 'jet-typography';

export default function TypographyScope() {
  const { pathname } = useLocation();

  // Also tags the page with its typography page group (data-jet-group), whose
  // overrides the published CSS applies on top of the site-wide values.
  useLayoutEffect(() => {
    const internal = isInternalPath(pathname);
    document.documentElement.classList.toggle(SCOPE_CLASS, !internal);
    setTypographyGroup(internal ? null : typographyGroupForPath(pathname));
  }, [pathname]);

  useLayoutEffect(
    () => () => {
      document.documentElement.classList.remove(SCOPE_CLASS);
      setTypographyGroup(null);
    },
    []
  );

  // Inside the dashboard's real-page preview, stop forms from submitting so an
  // admin clicking around can't send a real lead. A window capture listener runs
  // before React's root listener, so the app's onSubmit handlers never fire.
  useEffect(() => {
    if (!isTypographyPreviewFrame()) return undefined;
    const block = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener('submit', block, true);
    return () => window.removeEventListener('submit', block, true);
  }, []);

  // Local dev (CRA on :3000) has no server-side injection — fetch the same CSS.
  // Not in the preview frame: the dashboard injects the complete unpublished CSS
  // there, and a published block appended after it could win on equal specificity.
  useEffect(() => {
    if (document.getElementById(STYLE_ID) || isTypographyPreviewFrame()) return undefined;
    let cancelled = false;
    apigetPublic('typography/css').then((res) => {
      if (cancelled || res?.status !== 200 || typeof res.data !== 'string') return;
      if (document.getElementById(STYLE_ID)) return;
      const el = document.createElement('style');
      el.id = STYLE_ID;
      el.textContent = res.data;
      document.head.appendChild(el);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}
